import { z } from 'zod';
import { baseMessageSchema, createMessageId } from './_base';
import { clientTypeSchema, ClientType, matchmakingPlayerSchema } from './shared';
import { matchmakingSessionPresetSchema } from './matchmaking';

export enum ConnectionMessageType {
  CONN_INIT = 'CONN_INIT',
  CONN_AUTH_REQUIRED = 'CONN_AUTH_REQUIRED',
  CONN_INIT_SUCCESS = 'CONN_INIT_SUCCESS',
  CONN_INIT_ERROR = 'CONN_INIT_ERROR',
  CONN_CLOSE = 'CONN_CLOSE',
  CONN_CLOSED = 'CONN_CLOSED',
  CONN_FAST_START = 'CONN_FAST_START',
  CONN_FAST_START_SUCCESS = 'CONN_FAST_START_SUCCESS',
  CONN_FAST_START_ERROR = 'CONN_FAST_START_ERROR',
  CONN_BEACON_FAST_START = 'CONN_BEACON_FAST_START',
  CONN_BEACON_FAST_START_SUCCESS = 'CONN_BEACON_FAST_START_SUCCESS',
  CONN_BEACON_FAST_START_ERROR = 'CONN_BEACON_FAST_START_ERROR',
}

export type WebSocketId = string;
export type AuthId = string;
export type PersistentId = string;
export type MachineId = string;
export type HostSessionId = string;
export type ServerId = string;
export type InvitationId = string;
export type PlayerId = string;

export type PlayerConnection = {
  type: ClientType.PLAYER;
  websocketId: WebSocketId;
  authId?: AuthId;
  persistentId?: PersistentId;
  campaignId?: string;
  timestamp: number;
};

export type HostConnection = {
  type: ClientType.HOST;
  websocketId: WebSocketId;
  machineId: MachineId;
  authId?: AuthId;
  hostsessionId?: HostSessionId;
  isCloudHost?: boolean; // Server-validated cloud host status (determined during auth)
  timestamp: number;
};

export type RemoteHostConnection = {
  type: ClientType.REMOTE_HOST;
  websocketId: WebSocketId;
  machineId: MachineId;
  authId?: AuthId;
  timestamp: number;
};

export type RemoteClientConnection = {
  type: ClientType.REMOTE_CLIENT;
  websocketId: WebSocketId;
  persistentId?: PersistentId;
  authId?: AuthId;
  timestamp: number;
};

export type ObserverConnection = {
  type: ClientType.OBSERVER;
  websocketId: WebSocketId;
  persistentId?: PersistentId;
  authId?: AuthId;
  timestamp: number;
};

export type ServiceConnection = {
  type: ClientType.SERVICE;
  websocketId: WebSocketId;
  machineId: MachineId;
  authId: AuthId;
  timestamp: number;
};

export type AdminConnection = {
  type: ClientType.ADMIN;
  websocketId: WebSocketId;
  authId: AuthId;
  timestamp: number;
  persistentId?: PersistentId;
};

export type ServerInfo = {
  serverId: ServerId;
  lastPing: number;
  activeConnections: Set<WebSocketId>;
};

// Payload schemas
const connectionInitPayloadSchema = z.object({
  version: z.string(),
  // Add any other init data needed
});

const connectionAuthRequiredPayloadSchema = z.object({
  reason: z.string(),
  websocketId: z.string(),
});

const connectionInitSuccessPayloadSchema = z.object({});

const connectionInitErrorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

const connectionClosePayloadSchema = z.object({
  reason: z.string().optional(),
});

const connectionClosedPayloadSchema = z.object({
  reason: z.string().optional(),
});

const connFastStartPayloadSchema = z.object({
  version: z.string(),
  clientType: clientTypeSchema,
  persistentId: z.string().optional(),
  tokenJwt: z.string().optional(),
  anonymous: z.boolean().optional(),
  campaignId: z.string().optional(),
  playerInfo: matchmakingPlayerSchema,
  sessionPreset: matchmakingSessionPresetSchema,
  matchmakeToPublicMachine: z.boolean(),
  targetMachineId: z.string().optional(),
  searchPattern: z.array(z.string()).optional(),
  useSteamGuestLogin: z.boolean().optional(),
  regionOverride: z.string().optional(),
  requestIceServers: z.boolean().optional(),
});

const connFastStartSuccessPayloadSchema = z.object({
  websocketId: z.string(),
  userId: z.string(),
  invitationId: z.string(),
  queuePosition: z.number(),
  peerId: z.string().optional(),
  peerToken: z.string().optional(),
  iceServers: z
    .object({
      urls: z.array(z.string()),
      username: z.string(),
      credential: z.string(),
    })
    .optional(),
});

const connFastStartErrorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
  stage: z.enum(['init', 'auth', 'invitation', 'queue']),
});

const connBeaconFastStartPayloadSchema = z.object({
  version: z.string(),
  shareCode: z.string(),
  persistentId: z.string().optional(),
  tokenJwt: z.string().optional(),
  anonymous: z.boolean().optional(),
  requestIceServers: z.boolean().optional(),
});

const connBeaconFastStartSuccessPayloadSchema = z.object({
  websocketId: z.string(),
  userId: z.string(),
  hostPeerId: z.string(),
  peerId: z.string(),
  peerToken: z.string(),
  iceServers: z
    .object({
      urls: z.array(z.string()),
      username: z.string(),
      credential: z.string(),
    })
    .optional(),
  streamTitle: z.string().optional(),
});

const connBeaconFastStartErrorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
  stage: z.enum(['init', 'auth', 'resolve']),
});

// Full message schemas
const connectionInitSchema = baseMessageSchema
  .extend({
    type: z.literal(ConnectionMessageType.CONN_INIT),
  })
  .merge(connectionInitPayloadSchema);

const connectionAuthRequiredSchema = baseMessageSchema
  .extend({
    type: z.literal(ConnectionMessageType.CONN_AUTH_REQUIRED),
  })
  .merge(connectionAuthRequiredPayloadSchema);

const connectionInitSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(ConnectionMessageType.CONN_INIT_SUCCESS),
  })
  .merge(connectionInitSuccessPayloadSchema);

const connectionInitErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(ConnectionMessageType.CONN_INIT_ERROR),
  })
  .merge(connectionInitErrorPayloadSchema);

const connectionCloseSchema = baseMessageSchema
  .extend({
    type: z.literal(ConnectionMessageType.CONN_CLOSE),
  })
  .merge(connectionClosePayloadSchema);

const connectionClosedSchema = baseMessageSchema
  .extend({
    type: z.literal(ConnectionMessageType.CONN_CLOSED),
  })
  .merge(connectionClosedPayloadSchema);

const connFastStartSchema = baseMessageSchema
  .extend({
    type: z.literal(ConnectionMessageType.CONN_FAST_START),
  })
  .merge(connFastStartPayloadSchema);

const connFastStartSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(ConnectionMessageType.CONN_FAST_START_SUCCESS),
  })
  .merge(connFastStartSuccessPayloadSchema);

const connFastStartErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(ConnectionMessageType.CONN_FAST_START_ERROR),
  })
  .merge(connFastStartErrorPayloadSchema);

const connBeaconFastStartSchema = baseMessageSchema
  .extend({
    type: z.literal(ConnectionMessageType.CONN_BEACON_FAST_START),
  })
  .merge(connBeaconFastStartPayloadSchema);

const connBeaconFastStartSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(ConnectionMessageType.CONN_BEACON_FAST_START_SUCCESS),
  })
  .merge(connBeaconFastStartSuccessPayloadSchema);

const connBeaconFastStartErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(ConnectionMessageType.CONN_BEACON_FAST_START_ERROR),
  })
  .merge(connBeaconFastStartErrorPayloadSchema);

export const connectionMessageSchema = z.discriminatedUnion('type', [
  connectionInitSchema,
  connectionAuthRequiredSchema,
  connectionInitSuccessSchema,
  connectionInitErrorSchema,
  connectionCloseSchema,
  connectionClosedSchema,
  connFastStartSchema,
  connFastStartSuccessSchema,
  connFastStartErrorSchema,
  connBeaconFastStartSchema,
  connBeaconFastStartSuccessSchema,
  connBeaconFastStartErrorSchema,
]);

// Payload types
export type ConnectionInitPayload = z.infer<typeof connectionInitPayloadSchema>;
export type connectionAuthRequiredPayload = z.infer<typeof connectionAuthRequiredPayloadSchema>;
export type ConnectionInitSuccessPayload = z.infer<typeof connectionInitSuccessPayloadSchema>;
export type ConnectionInitErrorPayload = z.infer<typeof connectionInitErrorPayloadSchema>;
export type ConnectionClosePayload = z.infer<typeof connectionClosePayloadSchema>;
export type ConnectionClosedPayload = z.infer<typeof connectionClosedPayloadSchema>;
export type ConnFastStartPayload = z.infer<typeof connFastStartPayloadSchema>;
export type ConnFastStartSuccessPayload = z.infer<typeof connFastStartSuccessPayloadSchema>;
export type ConnFastStartErrorPayload = z.infer<typeof connFastStartErrorPayloadSchema>;
export type ConnBeaconFastStartPayload = z.infer<typeof connBeaconFastStartPayloadSchema>;
export type ConnBeaconFastStartSuccessPayload = z.infer<typeof connBeaconFastStartSuccessPayloadSchema>;
export type ConnBeaconFastStartErrorPayload = z.infer<typeof connBeaconFastStartErrorPayloadSchema>;

// Full message type
export type ConnectionMessage = z.infer<typeof connectionMessageSchema>;

type PayloadType<T extends ConnectionMessageType> = T extends ConnectionMessageType.CONN_INIT
  ? ConnectionInitPayload
  : T extends ConnectionMessageType.CONN_AUTH_REQUIRED
    ? connectionAuthRequiredPayload
    : T extends ConnectionMessageType.CONN_INIT_SUCCESS
      ? ConnectionInitSuccessPayload
      : T extends ConnectionMessageType.CONN_INIT_ERROR
        ? ConnectionInitErrorPayload
        : T extends ConnectionMessageType.CONN_CLOSE
          ? ConnectionClosePayload
          : T extends ConnectionMessageType.CONN_CLOSED
            ? ConnectionClosedPayload
            : T extends ConnectionMessageType.CONN_FAST_START
              ? ConnFastStartPayload
              : T extends ConnectionMessageType.CONN_FAST_START_SUCCESS
                ? ConnFastStartSuccessPayload
                : T extends ConnectionMessageType.CONN_FAST_START_ERROR
                  ? ConnFastStartErrorPayload
                  : T extends ConnectionMessageType.CONN_BEACON_FAST_START
                    ? ConnBeaconFastStartPayload
                    : T extends ConnectionMessageType.CONN_BEACON_FAST_START_SUCCESS
                      ? ConnBeaconFastStartSuccessPayload
                      : T extends ConnectionMessageType.CONN_BEACON_FAST_START_ERROR
                        ? ConnBeaconFastStartErrorPayload
                        : never;

export function generateConnectionMessage<T extends ConnectionMessageType>(type: T, payload: PayloadType<T>): ConnectionMessage {
  const base = {
    id: createMessageId(),
    timestamp: Date.now(),
    type,
  };

  switch (type) {
    case ConnectionMessageType.CONN_INIT:
      return connectionInitSchema.parse({
        ...base,
        ...payload,
      });
    case ConnectionMessageType.CONN_AUTH_REQUIRED:
      return connectionAuthRequiredSchema.parse({
        ...base,
        ...payload,
      });
    case ConnectionMessageType.CONN_INIT_SUCCESS:
      return connectionInitSuccessSchema.parse({
        ...base,
        ...payload,
      });
    case ConnectionMessageType.CONN_INIT_ERROR:
      return connectionInitErrorSchema.parse({
        ...base,
        ...payload,
      });
    case ConnectionMessageType.CONN_CLOSE:
      return connectionCloseSchema.parse({
        ...base,
        ...payload,
      });
    case ConnectionMessageType.CONN_CLOSED:
      return connectionClosedSchema.parse({
        ...base,
        ...payload,
      });
    case ConnectionMessageType.CONN_FAST_START:
      return connFastStartSchema.parse({
        ...base,
        ...payload,
      });
    case ConnectionMessageType.CONN_FAST_START_SUCCESS:
      return connFastStartSuccessSchema.parse({
        ...base,
        ...payload,
      });
    case ConnectionMessageType.CONN_FAST_START_ERROR:
      return connFastStartErrorSchema.parse({
        ...base,
        ...payload,
      });
    case ConnectionMessageType.CONN_BEACON_FAST_START:
      return connBeaconFastStartSchema.parse({
        ...base,
        ...payload,
      });
    case ConnectionMessageType.CONN_BEACON_FAST_START_SUCCESS:
      return connBeaconFastStartSuccessSchema.parse({
        ...base,
        ...payload,
      });
    case ConnectionMessageType.CONN_BEACON_FAST_START_ERROR:
      return connBeaconFastStartErrorSchema.parse({
        ...base,
        ...payload,
      });
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled message type: ${exhaustiveCheck}`);
    }
  }
}
